/**
 * How you have done.
 *
 * Read straight from the record the server keeps - see `recordPlayerStats`.
 * No RPC, because it is your own data; no write, because a record you can
 * write yourself is not a record.
 *
 * ## Games played, then games won, and in that order
 *
 * Played first because it is the true number - every finished game counts one
 * - and won second because it needs the first to mean anything. A win rate
 * shown alone flatters somebody who has played twice and won once.
 *
 * ## A rate is not shown under ten games
 *
 * Because it is not a rate yet. One game in two is 50% and says nothing; the
 * count says the same thing without pretending to be a measurement.
 */

import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCatalog } from '../use-catalog.js';
import { useSession } from '../session-context.js';
import { readStats } from '../net/player-record.js';
import type { PlayerStats } from '../net/player-record.js';
import { Icon } from '../render/Icon.js';
import { gameArt } from '../render/game-art.js';

/** Below this a percentage is noise dressed as a measurement. */
const RATE_NEEDS = 10;

export function Stats() {
  const { catalog } = useCatalog();
  const { session } = useSession();
  const [stats, setStats] = useState<PlayerStats | null>(null);

  useEffect(() => {
    if (!session) {
      setStats(null);
      return;
    }
    let live = true;
    void (async () => {
      const read = await readStats(session);
      if (live) setStats(read);
    })();
    return () => {
      live = false;
    };
  }, [session]);

  if (session === null) {
    return (
      <div className="page felt-page library-page">
        <h1 className="page-title page-heading">My Stats</h1>
        <p className="page-lede">Sign in and your games will start counting.</p>
      </div>
    );
  }

  if (!catalog || stats === null) return <div className="splash">Counting…</div>;

  /*
   * Only games actually played, most played first.
   *
   * The whole catalogue with zeroes beside it would be a list of things you
   * have not done, which is a different page and not one anybody asked for.
   */
  const rows = Object.keys(stats.games)
    .map((id) => ({
      id,
      name: catalog.games.find((game) => game.id === id)?.name ?? id,
      family: catalog.games.find((game) => game.id === id)?.family ?? '',
      ...(stats.games[id] as { played: number; won: number }),
    }))
    .filter((row) => row.played > 0)
    .sort((a, b) => b.played - a.played || a.name.localeCompare(b.name));

  return (
    <div className="page felt-page library-page">
      <h1 className="page-title page-heading">My Stats</h1>

      {stats.totals.played === 0 ? (
        <>
          <p className="page-lede">Nothing counted yet.</p>
          <p className="field-help">Finish a game and it will show up here.</p>
          <Link className="cta cta-primary" to="/library">
            <Icon name="gamepad" className="cta-mark" />
            <span className="cta-label">Your games</span>
            <Icon name="chevron-right" />
          </Link>
        </>
      ) : (
        <>
          <p className="page-lede">Every game you have played through to the end.</p>

          <div className="stat-totals">
            <Total label="Played" value={stats.totals.played} />
            <Total label="Won" value={stats.totals.won} />
            {stats.totals.played >= RATE_NEEDS ? (
              <Total
                label="Win rate"
                value={`${String(Math.round((stats.totals.won / stats.totals.played) * 100))}%`}
              />
            ) : null}
          </div>

          <ul className="stat-rows">
            {rows.map((row) => {
              const art = gameArt(row.id, row.family);
              return (
                <li key={row.id} className="stat-row">
                  <span className={`stat-row-mark tone-${art.tone}`} aria-hidden="true">
                    <Icon name={art.icon} />
                  </span>
                  <Link className="stat-row-name" to={`/game/${row.id}`}>
                    {row.name}
                  </Link>
                  <span className="stat-row-counts">
                    <span>
                      {row.played} played
                    </span>
                    <span className="stat-row-won">{row.won} won</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function Total({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat-total">
      <span className="stat-total-value">{value}</span>
      <span className="stat-total-label">{label}</span>
    </div>
  );
}
